import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import getDayRangeString from "../../../utils/day-range-string";
import getSelectedMetrics from "../../../utils/get-selected-metrics";

import { PERIOD_FORMATS } from "../../../constants";
import { useRillContext } from "../../../rill/RillReact/share/base";

const PERIOD_OPTIONS = (t) => ({
  day: [{ text: t("rawEquippedVolumeSplit.daily"), value: "day" }],
  week: [{ text: t("rawEquippedVolumeSplit.weekly"), value: "week" }],
  month: [{ text: t("rawEquippedVolumeSplit.monthly"), value: "month" }],
});

function View(props) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  _.defaults(locals.query, {
    type: "total",
    units: "metric",
  });

  const { snapshot, query, momentTZ } = locals;
  const { units } = query;
  const user = _.get(snapshot, "user", {});
  const exercisesVolumeSplitRawEquipped = _.get(
    snapshot,
    "data.exercisesVolumeSplitRawEquipped",
    {}
  );
  const period = (query.period = _.get(snapshot, "data.period", "day"));
  const period_short = PERIOD_FORMATS[period];
  const startDate = momentTZ(_.get(snapshot, "data.start", momentTZ()));
  const endDate = momentTZ(_.get(snapshot, "data.end", momentTZ()));
  const selectedMetrics = getSelectedMetrics(
    exercisesVolumeSplitRawEquipped,
    query.type
  );
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);

  const selectedMetricValues = _.values(selectedMetrics);

  const rawSeries = {
    name: "raw",
    className: "raw",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.raw`),
        units
      );
    }),
  };

  const equippedSeries = {
    name: "equipped",
    className: "equipped",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.equipped`),
        units
      );
    }),
  };

  const totalSeries = _.zipWith(
    rawSeries.data,
    equippedSeries.data,
    (a = 0, b = 0) => a + b
  );
  const volumeSeries = _.concat(rawSeries, equippedSeries);
  const maxVolume = _.max(totalSeries);

  return (
    <div className="body metrics">
      <h1 className="alt">{t("rawEquippedVolumeSplit.title")}</h1>
      <p>{t("rawEquippedVolumeSplit.description")}</p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="period"
                options={PERIOD_OPTIONS(t)[period]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("rawEquippedVolumeSplit.kg"), value: "metric" },
                  { text: t("rawEquippedVolumeSplit.lbs"), value: "imperial" },
                ]}
              />
            </div>
          </div>

          <div className="left-side">
            <div className="group">
              <ToggleLinksInput
                className="tabs"
                name="type"
                options={[
                  { text: t("rawEquippedVolumeSplit.total"), value: "total" },
                  { text: t("rawEquippedVolumeSplit.squat"), value: "squat" },
                  { text: t("rawEquippedVolumeSplit.bench"), value: "bench" },
                  { text: t("rawEquippedVolumeSplit.deadlift"), value: "deadlift" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="body">
          {!_.isEmpty(selectedMetrics) && (
            <div>
              <div className="legend">
                <div className="bar series raw">{t("rawEquippedVolumeSplit.rawVolume")}</div>
                <div className="bar series equipped">{t("rawEquippedVolumeSplit.equippedVolume")}</div>
              </div>
              <div className="axis-labels">
                <div className="left">{t("rawEquippedVolumeSplit.volume")}</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: volumeSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        scaleMinSpace: 30,
                        onlyInteger: true,
                        low: 0,
                        high: maxVolume,
                      },
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: labels,
                      series: volumeSeries,
                    }}
                    options={{
                      width: `${_.size(keys) * 40}px`,
                      height: "380px",
                      chartPadding: { left: 35 },
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                        low: 0,
                        high: maxVolume,
                      },
                    }}
                    tooltip={(item, i) => {
                      const key = keys[i];
                      const date = startDate.clone().add(key, period);
                      const dayRange = getDayRangeString(
                        [date, date.clone().add(1, period).subtract(1, "day")],
                        query
                      );
                      const rawTotal = _.round(rawSeries.data[i] || 0, 1);
                      const equippedTotal = _.round(
                        equippedSeries.data[i] || 0,
                        1
                      );
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            <div>
                              {t("rawEquippedVolumeSplit.raw")}{" "}
                              <b>{locals.withWeight(rawTotal, units)}</b>
                            </div>
                            <div>
                              {t("rawEquippedVolumeSplit.equipped")}{" "}
                              <b>{locals.withWeight(equippedTotal, units)}</b>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default View;
